// TabAsignacion — Mapa de carga de abogados (cantidad + monto).
//
// Para el director / subdirector titular: ve solo su área (filtro forzado por
// el backend). Para fiscal / delegado: ve todas las áreas y puede filtrar por
// una.
//
// El backend devuelve: resumen (cards), abogados (tabla ordenable), histograma
// de distribución por cantidad, top 10 causas por monto.
//
// Features:
//  - Rango de fechas (desde/hasta) sobre Fecha_Inicio del expediente.
//  - Histograma clickable: muestra los abogados del bucket en un Dialog.
//  - Export Excel (.xlsx) y PDF (jsPDF + autoTable).

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
    Box, Paper, Typography, CircularProgress, Alert, Stack,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
    TableSortLabel, Chip, Tooltip, MenuItem, TextField, LinearProgress,
    Button, Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
} from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CloseIcon from '@mui/icons-material/Close';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { dashEjecAsignacionJWT } from '../../api/endpoints';

const PRIMARY = '#1769aa';
const BR = 4;

const fmtNum = (n) => Number(n || 0).toLocaleString('es-AR', { maximumFractionDigits: 0 });
const fmtMonto = (n) => `$${Number(n || 0).toLocaleString('es-AR', { maximumFractionDigits: 0 })}`;

const BUCKETS = [
    { rango: '0–25', min: 0, max: 25 },
    { rango: '26–50', min: 26, max: 50 },
    { rango: '51–100', min: 51, max: 100 },
    { rango: '101–200', min: 101, max: 200 },
    { rango: '201+', min: 201, max: Number.POSITIVE_INFINITY },
];

function Card({ titulo, valor, subtitulo, color = PRIMARY }) {
    return (
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: `${BR}px`, borderTop: `3px solid ${color}` }}>
            <Typography variant="caption" sx={{ color: '#666', fontWeight: 600 }}>{titulo}</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color, lineHeight: 1.2 }}>{valor}</Typography>
            {subtitulo && <Typography variant="caption" sx={{ color: '#999' }}>{subtitulo}</Typography>}
        </Paper>
    );
}

export default function TabAsignacion({ acceso, onClickAbogado, desde, hasta, preset }) {
    const esDirector = acceso?.modo === 'DIRECTOR';
    const idAreaForzado = esDirector ? acceso?.idAreaForzado : null;

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [idAreaFiltro, setIdAreaFiltro] = useState(null);
    const [areasDisponibles, setAreasDisponibles] = useState([]);
    // El rango viene del selector global del header (props). preset 'mes' por
    // default — para "ver todo histórico" el Fiscal puede vaciar las fechas.
    // Bucket seleccionado para el dialog de "quiénes son".
    const [bucketAbierto, setBucketAbierto] = useState(null); // {rango, abogados}

    const idAreaEfectivo = esDirector ? idAreaForzado : idAreaFiltro;

    const cargar = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const r = await dashEjecAsignacionJWT(idAreaEfectivo, desde || null, hasta || null);
            if (r?.ok) {
                setData(r.data);
                if (!esDirector && areasDisponibles.length === 0 && idAreaEfectivo == null) {
                    const unicas = Array.from(new Set((r.data?.abogados || []).map((a) => a.area).filter(Boolean))).sort();
                    setAreasDisponibles(unicas);
                }
            } else {
                setError(r?.mensaje || 'Error al cargar asignación');
            }
        } catch (e) { setError(String(e)); }
        finally { setLoading(false); }
    }, [idAreaEfectivo, desde, hasta, esDirector, areasDisponibles.length]);

    useEffect(() => { cargar(); }, [cargar]);

    const [sortBy, setSortBy] = useState('cant_causas');
    const [sortDir, setSortDir] = useState('desc');
    const toggleSort = (col) => {
        if (sortBy === col) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
        else { setSortBy(col); setSortDir('desc'); }
    };
    const abogadosOrdenados = useMemo(() => {
        if (!data?.abogados) return [];
        const arr = [...data.abogados];
        arr.sort((a, b) => {
            const va = a[sortBy]; const vb = b[sortBy];
            const cmp = typeof va === 'number' && typeof vb === 'number'
                ? va - vb
                : String(va ?? '').localeCompare(String(vb ?? ''), 'es', { sensitivity: 'base' });
            return sortDir === 'desc' ? -cmp : cmp;
        });
        return arr;
    }, [data, sortBy, sortDir]);

    const maxCausas = useMemo(() => Math.max(1, ...(data?.abogados || []).map(a => a.cant_causas)), [data]);

    // Para hacer click en el histograma: agrupar abogados por bucket en frontend
    // (datos ya disponibles, evito otro round-trip).
    const abogadosPorBucket = useMemo(() => {
        const m = {};
        BUCKETS.forEach(b => { m[b.rango] = []; });
        (data?.abogados || []).forEach(a => {
            const b = BUCKETS.find(b => a.cant_causas >= b.min && a.cant_causas <= b.max);
            if (b) m[b.rango].push(a);
        });
        return m;
    }, [data]);

    const abrirBucket = (rango) => {
        const abogados = (abogadosPorBucket[rango] || []).slice().sort((a, b) => b.cant_causas - a.cant_causas);
        if (abogados.length > 0) setBucketAbierto({ rango, abogados });
    };

    // ─── EXPORTS ────────────────────────────────────────────────────
    // jsPDF con font helvetica default no soporta bien algunos caracteres
    // Unicode (em-dash, flechas) y los renderiza espaciados o como basura.
    // Reemplazo a equivalentes ASCII antes de pasarlos a doc.text(). Los
    // acentos sí los soporta (WinAnsi).
    const sanPDF = (s) => String(s ?? '')
        .replace(/—/g, '-')
        .replace(/–/g, '-')
        .replace(/→/g, 'a')
        .replace(/[“”]/g, '"')
        .replace(/[‘’]/g, "'");

    const nombreAreaActual = () => {
        // Prioridad: areaInfo del backend (autoritativo, viene aunque haya 0 abogados),
        // luego el primer abogado, luego un fallback genérico.
        const nombresApi = data?.areaInfo?.nombres || [];
        if (nombresApi.length > 0) return nombresApi.join(' / ');
        if (esDirector) return data?.abogados?.[0]?.area || 'Mi área';
        return idAreaFiltro != null ? (areasDisponibles.find(a => a === idAreaFiltro) || `Área ${idAreaFiltro}`) : 'Todas las áreas';
    };

    const tituloReporte = () => {
        const partes = ['Asignación de causas', nombreAreaActual()];
        if (desde || hasta) partes.push(`${desde || '...'} a ${hasta || '...'}`);
        return partes.join(' — ');
    };

    const handleExportExcel = () => {
        if (!data) return;
        const wb = XLSX.utils.book_new();
        const r = data.resumen || {};

        // Hoja Resumen
        const resumenData = [
            ['Asignación de causas'],
            ['Área', nombreAreaActual()],
            ['Período', (desde || hasta) ? `${desde || '...'} a ${hasta || '...'}` : 'Sin filtro de fecha'],
            ['Generado', new Date().toLocaleString('es-AR')],
            [],
            ['Concepto', 'Valor'],
            ['Abogados activos', Number(r.cant_abogados || 0)],
            ['Causas totales', Number(r.cant_causas || 0)],
            ['Monto bajo gestión', Number(r.monto_total || 0)],
            ['Promedio causas/abogado', Number(r.promedio_causas_por_abogado || 0)],
            ['Promedio $/causa', Number(r.promedio_monto_por_causa || 0)],
            ['Judiciales', Number(r.judiciales || 0)],
            ['Administrativos', Number(r.administrativos || 0)],
        ];
        const wsResumen = XLSX.utils.aoa_to_sheet(resumenData);
        wsResumen['!cols'] = [{ wch: 30 }, { wch: 40 }];
        // Merge del título en la fila 1
        wsResumen['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 1 } }];
        XLSX.utils.book_append_sheet(wb, wsResumen, 'Resumen');

        // Hoja Abogados
        const abogadosData = [
            ['Abogado', 'Matrícula', 'Área', 'Causas', 'Judiciales', 'Administrativos', 'Monto total', 'Antigüedad prom. (días)'],
            ...abogadosOrdenados.map(a => [
                a.nombre, a.matricula, a.area,
                Number(a.cant_causas || 0), Number(a.judiciales || 0), Number(a.administrativos || 0),
                Number(a.monto_total || 0), Number(a.antiguedad_promedio_dias || 0),
            ]),
        ];
        const wsAbog = XLSX.utils.aoa_to_sheet(abogadosData);
        wsAbog['!cols'] = [
            { wch: 38 }, { wch: 10 }, { wch: 32 },
            { wch: 9 }, { wch: 11 }, { wch: 16 }, { wch: 18 }, { wch: 18 },
        ];
        // Formato monto y enteros (col F=Monto índice 6, A=col 0)
        const headerRowOffset = 1;
        for (let i = 0; i < abogadosOrdenados.length; i++) {
            const row = i + headerRowOffset;
            const cellMonto = XLSX.utils.encode_cell({ r: row, c: 6 });
            if (wsAbog[cellMonto]) wsAbog[cellMonto].z = '"$"#,##0';
        }
        wsAbog['!freeze'] = { xSplit: 0, ySplit: 1 };
        XLSX.utils.book_append_sheet(wb, wsAbog, 'Abogados');

        // Hoja Top 10
        if ((data.topCausas || []).length > 0) {
            const topData = [
                ['Carátula', 'Autos/Exp.', 'Tipo', 'Monto', 'Abogado', 'Matrícula'],
                ...data.topCausas.map(c => [
                    c.caratula, c.autos, c.tipo === 1 ? 'Judicial' : 'Administrativo',
                    Number(c.monto || 0), c.nombre_abogado || '', c.matricula || '',
                ]),
            ];
            const wsTop = XLSX.utils.aoa_to_sheet(topData);
            wsTop['!cols'] = [
                { wch: 50 }, { wch: 22 }, { wch: 16 }, { wch: 18 }, { wch: 38 }, { wch: 10 },
            ];
            for (let i = 0; i < data.topCausas.length; i++) {
                const row = i + 1;
                const cellMonto = XLSX.utils.encode_cell({ r: row, c: 3 });
                if (wsTop[cellMonto]) wsTop[cellMonto].z = '"$"#,##0';
            }
            XLSX.utils.book_append_sheet(wb, wsTop, 'Top 10 causas');
        }

        const fecha = new Date().toISOString().slice(0, 10);
        XLSX.writeFile(wb, `Asignacion_${fecha}.xlsx`);
    };

    const handleExportPDF = () => {
        if (!data) return;
        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
        const r = data.resumen || {};
        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 14;

        // Título — wrappeado para no salirse del área imprimible.
        doc.setFontSize(14);
        doc.setTextColor(23, 105, 170);
        const tituloLineas = doc.splitTextToSize(sanPDF(tituloReporte()), pageWidth - margin * 2);
        doc.text(tituloLineas, margin, 15);
        const yPostTitulo = 15 + (tituloLineas.length - 1) * 6;

        doc.setFontSize(9);
        doc.setTextColor(120);
        doc.text(sanPDF(`Generado: ${new Date().toLocaleString('es-AR')}`), margin, yPostTitulo + 6);

        autoTable(doc, {
            startY: yPostTitulo + 11,
            head: [['Concepto', 'Valor']],
            body: [
                ['Abogados activos', fmtNum(r.cant_abogados)],
                ['Causas totales', fmtNum(r.cant_causas)],
                ['Monto bajo gestion', fmtMonto(r.monto_total)],
                ['Promedio causas/abogado', fmtNum(r.promedio_causas_por_abogado)],
                ['Promedio $/causa', fmtMonto(r.promedio_monto_por_causa)],
                ['Judiciales / Administrativos', `${fmtNum(r.judiciales)} / ${fmtNum(r.administrativos)}`],
            ],
            theme: 'grid',
            headStyles: { fillColor: [23, 105, 170] },
            styles: { fontSize: 9 },
            margin: { left: margin, right: margin },
        });

        doc.setFontSize(11);
        doc.setTextColor(60);
        doc.text(sanPDF(`Distribución por abogado (${fmtNum(abogadosOrdenados.length)})`), margin, doc.lastAutoTable.finalY + 8);
        autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 12,
            head: [['Abogado', 'Mat.', 'Área', 'Causas', 'Jud.', 'Adm.', 'Monto', 'Antig. (d)']],
            body: abogadosOrdenados.map(a => [
                sanPDF(a.nombre), a.matricula || '-', sanPDF(a.area || '-'),
                fmtNum(a.cant_causas), fmtNum(a.judiciales), fmtNum(a.administrativos),
                fmtMonto(a.monto_total), fmtNum(a.antiguedad_promedio_dias),
            ]),
            theme: 'striped',
            headStyles: { fillColor: [23, 105, 170] },
            styles: { fontSize: 8 },
            margin: { left: margin, right: margin },
        });

        if ((data.topCausas || []).length > 0) {
            doc.setFontSize(11);
            doc.setTextColor(60);
            doc.text('Top 10 causas por monto', margin, doc.lastAutoTable.finalY + 8);
            autoTable(doc, {
                startY: doc.lastAutoTable.finalY + 12,
                head: [['Carátula', 'Autos/Exp.', 'Tipo', 'Monto', 'Abogado']],
                body: data.topCausas.map(c => [
                    sanPDF(c.caratula || '-'), c.autos || '-',
                    c.tipo === 1 ? 'Jud.' : 'Adm.',
                    fmtMonto(c.monto),
                    sanPDF(c.nombre_abogado ? `${c.nombre_abogado}${c.matricula ? ' (' + c.matricula + ')' : ''}` : 'Sin asignar'),
                ]),
                theme: 'striped',
                headStyles: { fillColor: [46, 125, 50] },
                styles: { fontSize: 8 },
                margin: { left: margin, right: margin },
            });
        }

        // Preview en nueva pestaña (como el resto del sistema) en vez de
        // descargar directo. El usuario imprime / guarda desde el visor.
        const fecha = new Date().toISOString().slice(0, 10);
        const filename = `Asignacion_${fecha}.pdf`;
        const blob = doc.output('blob');
        const url = URL.createObjectURL(blob);
        const win = window.open(url, '_blank');
        if (!win) {
            // Popup bloqueado → fallback a descarga.
            doc.save(filename);
        }
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
    };

    if (loading && !data) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
    }
    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }
    if (!data) return null;

    const r = data.resumen || {};

    return (
        <Box>
            {/* Barra de filtros + exports */}
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
                {esDirector ? (
                    <>
                        <Typography variant="body2" sx={{ color: '#666' }}>Mi área:</Typography>
                        <Chip size="small" label={nombreAreaActual()} sx={{ fontWeight: 600 }} />
                    </>
                ) : (
                    <>
                        <Typography variant="body2" sx={{ color: '#666' }}>Área:</Typography>
                        <TextField select size="small" sx={{ minWidth: 220 }}
                            value={idAreaFiltro != null ? String(idAreaFiltro) : ''}
                            onChange={(e) => setIdAreaFiltro(e.target.value === '' ? null : Number(e.target.value))}
                        >
                            <MenuItem value="">Todas las áreas</MenuItem>
                            {areasDisponibles.map((a) => (
                                <MenuItem key={a} value={a}>{a}</MenuItem>
                            ))}
                        </TextField>
                    </>
                )}
                {(desde || hasta) && (
                    <Chip size="small" variant="outlined" label={`Período: ${desde || '...'} a ${hasta || '...'}`} sx={{ borderColor: PRIMARY, color: PRIMARY }} />
                )}
                {loading && <CircularProgress size={18} />}
                <Box sx={{ flexGrow: 1 }} />
                <Button size="small" variant="outlined" startIcon={<GetAppIcon />} onClick={handleExportExcel} disabled={!data}>Excel</Button>
                <Button size="small" variant="outlined" color="error" startIcon={<PictureAsPdfIcon />} onClick={handleExportPDF} disabled={!data}>PDF</Button>
            </Stack>

            {/* Cards */}
            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' }, mb: 2 }}>
                <Card titulo="Abogados activos" valor={fmtNum(r.cant_abogados)} subtitulo={`${fmtNum(r.cant_causas)} causas`} />
                <Card titulo="Monto bajo gestión" valor={fmtMonto(r.monto_total)} color="#2e7d32" />
                <Card titulo="Promedio causas / abogado" valor={fmtNum(r.promedio_causas_por_abogado)} color="#6a1b9a" />
                <Card titulo="Promedio $ / causa" valor={fmtMonto(r.promedio_monto_por_causa)} color="#0277bd" />
                <Card titulo="Judiciales / Admin" valor={`${fmtNum(r.judiciales)} / ${fmtNum(r.administrativos)}`} color="#37474f" />
            </Box>

            {/* Tabla de abogados */}
            <Paper variant="outlined" sx={{ borderRadius: `${BR}px`, mb: 2 }}>
                <Box sx={{ p: 1.5, borderBottom: '1px solid #eee' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#444' }}>
                        Distribución por abogado ({fmtNum(abogadosOrdenados.length)})
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#888' }}>
                        Click en una columna para ordenar. Click en un abogado para ver su detalle.
                    </Typography>
                </Box>
                <TableContainer>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ bgcolor: '#fafafa' }}>
                                    <TableSortLabel active={sortBy === 'nombre'} direction={sortBy === 'nombre' ? sortDir : 'asc'} onClick={() => toggleSort('nombre')}>Abogado</TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ bgcolor: '#fafafa' }}>Mat.</TableCell>
                                <TableCell sx={{ bgcolor: '#fafafa' }} align="right">
                                    <TableSortLabel active={sortBy === 'cant_causas'} direction={sortBy === 'cant_causas' ? sortDir : 'asc'} onClick={() => toggleSort('cant_causas')}>Causas</TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ bgcolor: '#fafafa', width: 120 }}>Carga</TableCell>
                                <TableCell sx={{ bgcolor: '#fafafa' }} align="right">Jud.</TableCell>
                                <TableCell sx={{ bgcolor: '#fafafa' }} align="right">Adm.</TableCell>
                                <TableCell sx={{ bgcolor: '#fafafa' }} align="right">
                                    <TableSortLabel active={sortBy === 'monto_total'} direction={sortBy === 'monto_total' ? sortDir : 'asc'} onClick={() => toggleSort('monto_total')}>Monto total</TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ bgcolor: '#fafafa' }} align="right">
                                    <TableSortLabel active={sortBy === 'antiguedad_promedio_dias'} direction={sortBy === 'antiguedad_promedio_dias' ? sortDir : 'asc'} onClick={() => toggleSort('antiguedad_promedio_dias')}>Antigüedad prom.</TableSortLabel>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {abogadosOrdenados.length === 0 && (
                                <TableRow><TableCell colSpan={8} align="center" sx={{ color: '#999', py: 3 }}>
                                    No hay abogados con causas activas en este filtro.
                                </TableCell></TableRow>
                            )}
                            {abogadosOrdenados.map(a => {
                                const pctCarga = (a.cant_causas / maxCausas) * 100;
                                const colorCarga = pctCarga >= 80 ? '#c62828' : pctCarga >= 50 ? '#e65100' : '#2e7d32';
                                return (
                                    <TableRow key={a.idPersonal} hover sx={{ cursor: onClickAbogado ? 'pointer' : 'default' }}
                                        onClick={() => onClickAbogado?.(a.idPersonal)}>
                                        <TableCell>{a.nombre}</TableCell>
                                        <TableCell><Typography variant="caption" sx={{ color: '#666' }}>{a.matricula || '-'}</Typography></TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtNum(a.cant_causas)}</TableCell>
                                        <TableCell sx={{ width: 120 }}>
                                            <Tooltip title={`${Math.round(pctCarga)}% del máximo del área`}>
                                                <LinearProgress variant="determinate" value={pctCarga}
                                                    sx={{ height: 8, borderRadius: 4, '& .MuiLinearProgress-bar': { bgcolor: colorCarga } }} />
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell align="right">{fmtNum(a.judiciales)}</TableCell>
                                        <TableCell align="right">{fmtNum(a.administrativos)}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtMonto(a.monto_total)}</TableCell>
                                        <TableCell align="right">{fmtNum(a.antiguedad_promedio_dias)} días</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Histograma de distribución (click para ver quiénes) */}
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: `${BR}px`, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#444', mb: 0.5 }}>
                    Distribución de carga (abogados según cantidad de causas)
                </Typography>
                <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1 }}>
                    Click en una barra para ver los abogados de ese rango.
                </Typography>
                <Stack direction="row" alignItems="flex-end" spacing={1} sx={{ height: 160, mb: 0.5 }}>
                    {(() => {
                        const maxAbog = Math.max(1, ...(data.histograma || []).map(h => h.cant_abogados));
                        const altoArea = 130; // px disponibles para barras
                        return (data.histograma || []).map((h, i) => {
                            const tieneDato = h.cant_abogados > 0;
                            // Altura proporcional con piso mínimo legible cuando hay datos.
                            const alturaProporcional = (h.cant_abogados / maxAbog) * altoArea;
                            const altura = tieneDato ? Math.max(18, alturaProporcional) : 0;
                            // Etiqueta arriba de la barra cuando es chica (no entra adentro).
                            const etiquetaAfuera = altura < 28;
                            return (
                                <Box key={i} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
                                    {etiquetaAfuera && tieneDato && (
                                        <Typography variant="caption" sx={{ color: '#333', fontWeight: 700, fontSize: 11, mb: 0.3 }}>
                                            {h.cant_abogados}
                                        </Typography>
                                    )}
                                    <Tooltip title={tieneDato ? `${h.cant_abogados} abogado${h.cant_abogados === 1 ? '' : 's'} — click para ver` : 'Sin abogados en este rango'}>
                                        <Box
                                            onClick={() => tieneDato && abrirBucket(h.rango)}
                                            sx={{
                                                width: '100%', height: `${altura}px`,
                                                bgcolor: tieneDato ? PRIMARY : '#e0e0e0',
                                                borderRadius: '3px 3px 0 0',
                                                cursor: tieneDato ? 'pointer' : 'default',
                                                display: 'flex', alignItems: 'flex-start', justifyContent: 'center', pt: 0.5,
                                                transition: 'opacity 0.15s',
                                                '&:hover': tieneDato ? { opacity: 0.85 } : {},
                                            }}
                                        >
                                            {!etiquetaAfuera && tieneDato && (
                                                <Typography variant="caption" sx={{ color: 'white', fontWeight: 700, fontSize: 11 }}>
                                                    {h.cant_abogados}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Tooltip>
                                </Box>
                            );
                        });
                    })()}
                </Stack>
                <Stack direction="row" spacing={1}>
                    {(data.histograma || []).map((h, i) => (
                        <Box key={i} sx={{ flex: 1, textAlign: 'center' }}>
                            <Typography variant="caption" sx={{ color: '#666' }}>{h.rango}</Typography>
                        </Box>
                    ))}
                </Stack>
                <Typography variant="caption" sx={{ color: '#888', display: 'block', mt: 0.5 }}>
                    Cantidad de causas por abogado.
                </Typography>
            </Paper>

            {/* Top 10 causas grandes */}
            <Paper variant="outlined" sx={{ borderRadius: `${BR}px` }}>
                <Box sx={{ p: 1.5, borderBottom: '1px solid #eee' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#444' }}>
                        Top 10 causas por monto
                    </Typography>
                </Box>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ bgcolor: '#fafafa' }}>Carátula</TableCell>
                                <TableCell sx={{ bgcolor: '#fafafa' }}>Autos / Exp.</TableCell>
                                <TableCell sx={{ bgcolor: '#fafafa' }} align="center">Tipo</TableCell>
                                <TableCell sx={{ bgcolor: '#fafafa' }} align="right">Monto</TableCell>
                                <TableCell sx={{ bgcolor: '#fafafa' }}>Abogado asignado</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(data.topCausas || []).length === 0 && (
                                <TableRow><TableCell colSpan={5} align="center" sx={{ color: '#999', py: 2 }}>
                                    Sin causas para mostrar.
                                </TableCell></TableRow>
                            )}
                            {(data.topCausas || []).map(c => (
                                <TableRow key={c.id} hover>
                                    <TableCell>{c.caratula || '-'}</TableCell>
                                    <TableCell>{c.autos || '-'}</TableCell>
                                    <TableCell align="center">
                                        <Chip size="small" label={c.tipo === 1 ? 'Jud.' : 'Adm.'} color={c.tipo === 1 ? 'primary' : 'default'} variant="outlined" />
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtMonto(c.monto)}</TableCell>
                                    <TableCell>
                                        {c.idPersonal ? (
                                            <Typography variant="body2" sx={{ cursor: onClickAbogado ? 'pointer' : 'default', color: onClickAbogado ? PRIMARY : 'inherit', textDecoration: onClickAbogado ? 'underline' : 'none' }}
                                                onClick={() => onClickAbogado?.(c.idPersonal)}>
                                                {c.nombre_abogado} {c.matricula ? `(${c.matricula})` : ''}
                                            </Typography>
                                        ) : <em style={{ color: '#999' }}>Sin asignar</em>}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Dialog: abogados del bucket seleccionado */}
            <Dialog open={!!bucketAbierto} onClose={() => setBucketAbierto(null)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Abogados con {bucketAbierto?.rango} causas
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#888' }}>
                            {bucketAbierto?.abogados?.length || 0} abogado{bucketAbierto?.abogados?.length === 1 ? '' : 's'}
                        </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => setBucketAbierto(null)}><CloseIcon fontSize="small" /></IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ bgcolor: '#fafafa' }}>Abogado</TableCell>
                                <TableCell sx={{ bgcolor: '#fafafa' }} align="right">Causas</TableCell>
                                <TableCell sx={{ bgcolor: '#fafafa' }} align="right">Monto</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(bucketAbierto?.abogados || []).map(a => (
                                <TableRow key={a.idPersonal} hover
                                    sx={{ cursor: onClickAbogado ? 'pointer' : 'default' }}
                                    onClick={() => {
                                        if (onClickAbogado) {
                                            onClickAbogado(a.idPersonal);
                                            setBucketAbierto(null);
                                        }
                                    }}>
                                    <TableCell>
                                        <Typography variant="body2">{a.nombre}</Typography>
                                        <Typography variant="caption" sx={{ color: '#999' }}>{a.matricula || '-'}</Typography>
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtNum(a.cant_causas)}</TableCell>
                                    <TableCell align="right">{fmtMonto(a.monto_total)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBucketAbierto(null)}>Cerrar</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
